import React, { forwardRef, RefObject, useId, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useSiteCountry } from '@agria/theme/src/hooks/useSiteCountry';
import { BaseFormProps, StyledBase } from '../base/Base';
import { FormField, FormFieldProps } from '../formField/FormField';
import { Currency, CurrencyIe } from '../../icon';

export interface InputProps extends BaseFormProps, FormFieldProps {
  type?:
    | 'text'
    | 'email'
    | 'password'
    | 'tel'
    | 'search'
    | 'number'
    | 'hidden'
    | 'file';
  currency?: boolean;
  maxLength?: number;
  showPassword?: boolean;
  autoComplete?: string;
  accept?: string;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'tel';
}

// Styles

const StyledCurrency = styled.div`
  ${({ theme: { space } }) => css`
    position: relative;
    input {
      padding-left: ${space.xLarge};
    }
  `};
`;

const StyledCurrencyIcon = styled(({ isIe, ...props }) =>
  isIe ? <CurrencyIe {...props} /> : <Currency {...props} />
)`
  ${({ theme: { space, layers } }) => css`
    position: absolute;
    top: 50%;
    left: ${space.small};
    transform: translateY(-50%);
    pointer-events: none;
    z-index: ${layers.currencyInput};
  `};
`;

const StyledBaseWrapper = styled.div`
  ${({ theme: { colors } }) => css`
    position: relative;
    display: flex;
    width: 100%;

    & > input,
    & > div {
      flex: 1;
    }

    & > button {
      position: absolute;
      height: 100%;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      cursor: pointer;
      text-transform: uppercase;
      font-size: 14px;
      line-height: 18px;
      padding-inline: 16px;
      z-index: 2147483648;
      color: ${colors.brandOne100};
    }
  `};
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      id,
      type = 'text',
      invalid,
      valid,
      defaultValue,
      placeholder,
      required,
      errorMessage,
      hideLabel,
      disabled,
      hintText,
      optional,
      extraInformation,
      hidden,
      value,
      currency,
      expanderTitle,
      expanderContent,
      onChange,
      maxLength,
      onFocus,
      showPassword,
      autoComplete,
      readOnly,
      accept,
      inputMode,
      ...rest
    },
    ref
  ) => {
    const [passwordShow, setPasswordShow] = useState(false);

    const changePasswordInputType = () => {
      setPasswordShow(!passwordShow);
    };

    const errorMessageElementId = useId();

    const { isIe } = useSiteCountry();

    return (
      <FormField
        fieldAs="fieldset"
        label={label}
        htmlFor={id}
        invalid={invalid}
        errorMessage={errorMessage}
        errorMessageElementId={errorMessageElementId}
        hideLabel={hideLabel}
        hintText={hintText}
        disabled={disabled}
        optional={optional}
        extraInformation={extraInformation}
        hidden={hidden}
        expanderTitle={expanderTitle}
        expanderContent={expanderContent}
        required={required}
        {...rest}
      >
        {currency && (
          <StyledCurrency>
            <StyledCurrencyIcon isIe={isIe} />
            <StyledBase
              as="input"
              type={hidden ? 'hidden' : type}
              id={id}
              name={id}
              invalid={invalid}
              valid={valid}
              value={value}
              ref={ref as RefObject<HTMLInputElement>}
              placeholder={placeholder}
              defaultValue={
                typeof defaultValue === 'boolean' ? undefined : defaultValue
              }
              disabled={disabled}
              required={required}
              aria-label={label}
              aria-required={required}
              aria-labelledby={id}
              aria-describedby={`${hintText ? `${id}-hint` : ''}${
                errorMessageElementId && `${errorMessageElementId}`
              }`.trim()}
              aria-invalid={Boolean(invalid || errorMessage)}
              onChange={onChange}
              maxLength={maxLength}
              onFocus={onFocus}
              autoComplete={autoComplete}
              aria-readonly={readOnly}
              readOnly={readOnly}
              inputMode={inputMode}
            />
          </StyledCurrency>
        )}
        {!currency && !showPassword && (
          <StyledBase
            as="input"
            type={hidden ? 'hidden' : type}
            id={id}
            name={id}
            invalid={invalid}
            valid={valid}
            value={value}
            ref={ref as RefObject<HTMLInputElement>}
            placeholder={placeholder}
            defaultValue={
              typeof defaultValue === 'boolean' ? undefined : defaultValue
            }
            disabled={disabled}
            required={required}
            aria-required={required}
            aria-labelledby={id}
            aria-describedby={`${hintText ? `${id}-hint` : ''}${
              errorMessageElementId && `${errorMessageElementId}`
            }`.trim()}
            aria-invalid={Boolean(invalid || errorMessage)}
            onChange={onChange}
            maxLength={maxLength}
            onFocus={onFocus}
            autoComplete={autoComplete}
            aria-readonly={readOnly}
            readOnly={readOnly}
            inputMode={inputMode}
            {...(accept ? { accept } : {})}
          />
        )}
        {!currency && showPassword && (
          <StyledBaseWrapper>
            <StyledBase
              as="input"
              type={passwordShow ? 'text' : 'password'}
              id={id}
              name={id}
              invalid={invalid}
              valid={valid}
              value={value}
              ref={ref as RefObject<HTMLInputElement>}
              placeholder={placeholder}
              defaultValue={
                typeof defaultValue === 'boolean' ? undefined : defaultValue
              }
              disabled={disabled}
              required={required}
              aria-required={required}
              aria-labelledby={id}
              aria-describedby={`${hintText ? `${id}-hint` : ''}${
                errorMessageElementId && `${errorMessageElementId}`
              }`.trim()}
              aria-invalid={Boolean(invalid || errorMessage)}
              onChange={onChange}
              maxLength={maxLength}
              onFocus={onFocus}
              autoComplete={autoComplete}
              aria-readonly={readOnly}
              readOnly={readOnly}
              inputMode={inputMode}
            />
            <button type="button" onClick={changePasswordInputType}>
              {passwordShow ? 'Hide' : 'Show'}
            </button>
          </StyledBaseWrapper>
        )}
      </FormField>
    );
  }
);
Input.displayName = 'Input';
